"use client"

import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { usePathname } from "next/navigation"
import type { components } from "umbraco"
import { getMediaUrl, getLinkUrl, getLinkTarget } from "umbraco"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { getNonUmbracoUrl } from "utils"
import type { HeaderData, MenuItemData } from "../Header"
import { useContactModal } from "../ContactModal/context"
import CloseIcon from "../../icons/CloseIcon"
import SearchIcon from "../../icons/SearchIcon"
import ArrowLeft from "../../icons/ArrowLeft"
import GradientChevron from "../../icons/GradientChevron"
import Plus from "../../icons/Plus"
import Minus from "../../icons/Minus"
import {
  Backdrop,
  DrawerPanel,
  ScrollWrapper,
  DrawerHeaderBar,
  MiddleArea,
  HeaderIconGroup,
  HeaderIconButton,
  HeaderLogoLink,
  HeaderLogoImage,
  HeaderCtaButton,
  HeaderCtaButtonAsButton,
  MenuItems,
  MenuItem,
  MenuLink,
  MenuButton,
  MenuTitle,
  MenuTitleGroup,
  HoverChevron,
  ExpandIconWrapper,
  MenuItemWrapper,
  SubMenu,
  SubLink,
  QuickLinksSection,
  QuickLinksLabel,
  QuickLinkItem,
  BottomStrip,
  CtaButton,
  SocialRow,
  SocialLink,
  EmailLink,
} from "./styles"

type SideMenuProperties = components["schemas"]["SideMenuPropertiesModel"]
type SideMenuQuickLinkProperties =
  components["schemas"]["SideMenuQuickLinkPropertiesModel"]
type SocialLinkItemProperties =
  components["schemas"]["SocialLinkItemPropertiesModel"]

export interface SideMenuData {
  properties: SideMenuProperties | null
}

interface SideMenuProps {
  data?: SideMenuData | null
  headerData?: HeaderData | null
  isOpen: boolean
  onClose: () => void
  onOpenSearch?: () => void
}

/**
 * Normalize a pathname for comparison:
 * strip locale prefix (/he/, /en/) and trailing slash
 */
function normalizePath(path: string): string {
  let normalized = path.replace(/^\/(he|en)(\/|$)/, "/")
  if (normalized !== "/" && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1)
  }
  return normalized
}

export default function SideMenu({ data, headerData, isOpen, onClose, onOpenSearch }: SideMenuProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const pathname = usePathname()
  const { open: openContactModal, isEnabled: isContactModalEnabled } =
    useContactModal()

  // ESEIM-344: Use headerData.menuItems as the single source of truth for navigation
  const menuItems: MenuItemData[] = headerData?.menuItems ?? []

  // ESEIM-322/323: Detect active sub-item and auto-expand its parent category
  const { activeMatchIndex } = useMemo(() => {
    const normalizedPathname = normalizePath(pathname ?? "")

    for (let i = 0; i < menuItems.length; i++) {
      const item = menuItems[i]
      const children = item.children ?? []

      for (const child of children) {
        const childHref = child.link ? getNonUmbracoUrl(child.link) : null
        if (
          childHref &&
          childHref !== "/" &&
          normalizePath(childHref) === normalizedPathname
        ) {
          return { activeMatchIndex: i }
        }
      }

      // Also check if the category's own link matches the current page
      const catHref = item.link ? getNonUmbracoUrl(item.link) : null
      if (
        catHref &&
        catHref !== "/" &&
        normalizePath(catHref) === normalizedPathname
      ) {
        return { activeMatchIndex: i }
      }
    }
    return { activeMatchIndex: null }
  }, [menuItems, pathname])

  useEffect(() => {
    if (isOpen && activeMatchIndex !== null) {
      setExpandedIndex(activeMatchIndex)
    }
  }, [isOpen, activeMatchIndex])

  useEffect(() => {
    if (!isOpen) return
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [isOpen])

  // EGDE-655: closing the drawer only translates it off-screen, so its scroll
  // region kept whatever offset the visitor left behind and the next open
  // started mid-list. Rewind every scrollable inside the panel on open —
  // that is ScrollWrapper on mobile and MiddleArea on desktop, and taking them
  // both keeps this correct across the breakpoint without duplicating the
  // layout's own rules here.
  useEffect(() => {
    if (!isOpen || !panelRef.current) return
    for (const el of panelRef.current.querySelectorAll<HTMLElement>("*")) {
      if (el.scrollTop !== 0) el.scrollTop = 0
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !panelRef.current) return
    const panel = panelRef.current
    const focusableSelector =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose()
        return
      }
      if (e.key !== "Tab") return
      const focusable = panel.querySelectorAll<HTMLElement>(focusableSelector)
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    const closeBtn = panel.querySelector<HTMLElement>("button")
    closeBtn?.focus()

    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, onClose])

  const handleAccordionToggle = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index))
  }

  const handleLinkClick = useCallback(() => {
    onClose()
    setExpandedIndex(null)
  }, [onClose])

  const properties = data?.properties
  if (!properties) return null

  const headerProps = headerData?.properties
  const headerLogoUrl = headerProps ? getMediaUrl(headerProps.logo) : null
  const headerLogoSrc = headerLogoUrl ? getUmbracoMediaUrl(headerLogoUrl) : null
  // EGDE-320: `contactCta` is a TextBox (label only) in the CMS, so it never
  // resolves as a link. Mirror the Header: derive the label from the string (or
  // a link's title) and open the global contact modal when enabled, falling
  // back to a URL only if one is authored.
  const contactCtaRaw = headerProps?.contactCta as
    | string
    | components["schemas"]["ApiLinkModel"][]
    | null
    | undefined
  const headerCtaTitle =
    typeof contactCtaRaw === "string"
      ? contactCtaRaw
      : (contactCtaRaw?.[0]?.title ?? "")
  const headerCtaUrl = getLinkUrl(headerProps?.contactCta)

  const quickLinks = properties.quickLinks?.items ?? []
  const quickLinksLabel = properties.quickLinksLabel
  const ctaLabel = properties.ctaLabel
  const ctaUrl = getLinkUrl(properties.ctaLink)
  const contactEmail = properties.contactEmail
  const socialItems = properties.socialLinks?.items ?? []

  return (
    <>
      <Backdrop
        $isOpen={isOpen}
        onClick={onClose}
        data-component="side-menu-backdrop"
      />
      <DrawerPanel
        ref={panelRef}
        $isOpen={isOpen}
        role="dialog"
        aria-modal={true}
        aria-label={properties.menuTitle ?? "Navigation menu"}
        data-component="side-menu"
      >
        <ScrollWrapper>
        <DrawerHeaderBar>
          {/* RTL row: Icons first in JSX → left visually; Logo center; CTA last → right visually */}
          <HeaderIconGroup>
            <HeaderIconButton onClick={onClose} aria-label="Close menu">
              <CloseIcon />
            </HeaderIconButton>
            <HeaderIconButton
              aria-label="Search"
              onClick={() => {
                onClose()
                onOpenSearch?.()
              }}
            >
              <SearchIcon />
            </HeaderIconButton>
          </HeaderIconGroup>

          {headerLogoSrc && (
            <HeaderLogoLink href="/" onClick={handleLinkClick}>
              <HeaderLogoImage src={headerLogoSrc} alt="Egged Plus" />
            </HeaderLogoLink>
          )}

          {/* EGDE-320: contact CTA on the top inline-end of the side menu.
              Opens the global contact modal when enabled, otherwise links. */}
          {headerCtaTitle &&
            (isContactModalEnabled ? (
              <HeaderCtaButtonAsButton
                type="button"
                aria-haspopup="dialog"
                onClick={() => {
                  onClose()
                  openContactModal()
                }}
              >
                {headerCtaTitle}
              </HeaderCtaButtonAsButton>
            ) : (
              headerCtaUrl && (
                <HeaderCtaButton
                  href={getNonUmbracoUrl(headerCtaUrl)}
                  onClick={handleLinkClick}
                >
                  {headerCtaTitle}
                </HeaderCtaButton>
              )
            ))}
        </DrawerHeaderBar>

        {/* EGDE-498/EGDE-653: header stays pinned above, the bottom strip below
            the divider; everything in between is the desktop scroll region. */}
        <MiddleArea>
        {/* ESEIM-344: Categories now come from headerData.menuItems (single source of truth) */}
        <MenuItems
          role="navigation"
          aria-label={properties.menuTitle ?? undefined}
        >
          {menuItems.map((item, index) => {
            const label = item.title
            const linkUrl = item.link
            const children = item.children ?? []
            const hasChildren = children.length > 0
            const isExpanded = expandedIndex === index
            const isCategoryActive = activeMatchIndex === index

            if (!hasChildren) {
              return (
                <MenuItem key={label || index}>
                  <MenuLink
                    href={getNonUmbracoUrl(linkUrl ?? undefined)}
                    onClick={handleLinkClick}
                    $active={isCategoryActive}
                    target={item.target}
                    rel={
                      item.target === "_blank"
                        ? "noopener noreferrer"
                        : undefined
                    }
                  >
                    <MenuTitleGroup>
                      <HoverChevron aria-hidden="true">
                        <GradientChevron />
                      </HoverChevron>
                      <MenuTitle variant="body1">{label}</MenuTitle>
                    </MenuTitleGroup>
                  </MenuLink>
                </MenuItem>
              )
            }

            return (
              <MenuItem key={label || index}>
                <MenuItemWrapper $expanded={isExpanded}>
                  <MenuButton
                    onClick={() => handleAccordionToggle(index)}
                    aria-expanded={isExpanded}
                    aria-controls={`side-menu-sub-${index}`}
                    $expanded={isExpanded}
                  >
                    <MenuTitleGroup>
                      <HoverChevron aria-hidden="true">
                        <GradientChevron />
                      </HoverChevron>
                      <MenuTitle variant="body1">{label}</MenuTitle>
                    </MenuTitleGroup>
                    <ExpandIconWrapper $expanded={isExpanded}>
                      {isExpanded ? (
                        <Minus width={20} height={20} />
                      ) : (
                        <Plus width={20} height={20} />
                      )}
                    </ExpandIconWrapper>
                  </MenuButton>
                  <SubMenu $expanded={isExpanded} id={`side-menu-sub-${index}`}>
                    {children.map((child) => {
                      const subLabel = child.title
                      const subHref = getNonUmbracoUrl(child.link ?? undefined)
                      const isActive =
                        subHref !== "/" &&
                        normalizePath(subHref) === normalizePath(pathname ?? "")
                      return (
                        <li key={subLabel}>
                          <SubLink
                            href={subHref}
                            onClick={handleLinkClick}
                            aria-current={isActive ? "page" : undefined}
                            $active={isActive}
                            target={child.target}
                            rel={
                              child.target === "_blank"
                                ? "noopener noreferrer"
                                : undefined
                            }
                          >
                            {subLabel}
                          </SubLink>
                        </li>
                      )
                    })}
                  </SubMenu>
                </MenuItemWrapper>
              </MenuItem>
            )
          })}
        </MenuItems>

        {quickLinks.length > 0 && (
          <QuickLinksSection>
            {quickLinksLabel && (
              <QuickLinksLabel>{quickLinksLabel}</QuickLinksLabel>
            )}
            {quickLinks.map((ql) => {
              const qlProps = ql.content?.properties as
                | SideMenuQuickLinkProperties
                | undefined
              if (!qlProps) return null
              const qlKey = ql.content?.id ?? qlProps.label
              const qlLabel = qlProps.label ?? ""
              // EGDE-569: internal picks resolve to the raw Umbraco route
              // ("/pages/terms-of-use/") — strip the /pages prefix like every
              // other internal link; absolute external URLs pass through.
              const qlRawUrl = getLinkUrl(qlProps.link)
              const qlLinkUrl = qlRawUrl ? getNonUmbracoUrl(qlRawUrl) : null
              const qlTarget = getLinkTarget(qlProps.link) ?? "_blank"

              return (
                <QuickLinkItem
                  key={qlKey}
                  href={qlLinkUrl ?? "#"}
                  target={qlTarget}
                  rel="noopener noreferrer"
                  onClick={handleLinkClick}
                >
                  {/* ESEIM-335: Label first, then arrow icon.
                      EGDE-498: RTL/LTR flip + hover/focus nudge handled in CSS. */}
                  {qlLabel}
                  <ArrowLeft />
                </QuickLinkItem>
              )
            })}
          </QuickLinksSection>
        )}

        {/* EGDE-669: the language pill (EGDE-342/EGDE-498) is gone from the
            scroll content — English was discarded, so the drawer ends with the
            quick links above the bottom strip's divider. */}
        </MiddleArea>

        <BottomStrip>
          {ctaUrl && ctaLabel && (
            <CtaButton
              href={getNonUmbracoUrl(ctaUrl)}
              onClick={handleLinkClick}
              // EGDE-570: honour the CMS picker's "open in new window" — the
              // target never reached the anchor.
              target={getLinkTarget(properties.ctaLink) ?? undefined}
              rel={
                getLinkTarget(properties.ctaLink) === "_blank"
                  ? "noopener noreferrer"
                  : undefined
              }
            >
              {ctaLabel}
            </CtaButton>
          )}

          {/* ESEIM-324/325: Icons driven by platform dropdown, not media picker */}
          {socialItems.length > 0 && (
            <SocialRow>
              {socialItems.map((item) => {
                // EGDE-306: the social icon comes from the Umbraco `icon` media
                // field (SVG). EGDE-310: the accessible label now comes from the
                // `platform` dropdown (string array), falling back to the legacy
                // free-text label. Widen the cast for both.
                const socialProps = item.content?.properties as
                  | (SocialLinkItemProperties & {
                      icon?: unknown
                      platform?: string[] | string | null
                      label?: string | null
                    })
                  | undefined
                if (!socialProps) return null

                const socialKey = item.content?.id
                const socialLinkUrl = getLinkUrl(socialProps.link)
                const linkTarget = getLinkTarget(socialProps.link) ?? "_blank"
                // EGDE-348: a single-select DropDown.Flexible is a STRING in the
                // Delivery API, not an array — `platform?.[0]` would take the
                // first CHARACTER (e.g. "W"), so the aria-label no longer matched
                // Umbraco. Normalise array/string before falling back to label.
                const platformValue = Array.isArray(socialProps.platform)
                  ? socialProps.platform[0]
                  : socialProps.platform
                const socialLabel = platformValue || socialProps.label || ""

                const iconUrl = getMediaUrl(socialProps.icon)
                const iconSrc = iconUrl ? getUmbracoMediaUrl(iconUrl) : null
                if (!iconSrc) return null

                return (
                  <SocialLink
                    key={socialKey}
                    href={socialLinkUrl ?? "#"}
                    target={linkTarget}
                    rel="noopener noreferrer"
                    aria-label={socialLabel || "Social link"}
                  >
                    <img src={iconSrc} alt={socialLabel} />
                  </SocialLink>
                )
              })}
            </SocialRow>
          )}

          {contactEmail && (
            <EmailLink href={`mailto:${contactEmail}`}>
              {contactEmail}
            </EmailLink>
          )}
        </BottomStrip>
        </ScrollWrapper>
      </DrawerPanel>
    </>
  )
}
